import React, {useCallback, useRef, useEffect} from 'react';
import {View, LayoutChangeEvent} from 'react-native';
import styled from 'styled-components/native';
import {useTheme} from 'styled-components';
import {Theme} from '../../theme/lightTheme';
import Icon from '../icon';
import MyPressable from '../myPressable';
import TextGeneric from '../textGeneric';
import {AccordionItemProps} from '../../interfaces/components/accordionItem';
import useResponsiveDimensions from '../../utils/hooks/useResponsiveDimensions';
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withTiming,
  interpolate,
  useDerivedValue,
  interpolateColor,
  withSpring,
  withDelay,
  Easing,
} from 'react-native-reanimated';

const Container = styled(Animated.View)`
  width: 100%;
  overflow: hidden;
  background-color: ${({theme}: {theme: Theme}) => theme.cardBackground};
  border-radius: 16px;
  border-width: 1px;
`;
const HeaderContainer = styled.View`
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: ${({theme}) => theme.text.s4}px ${({theme}) => theme.text.s8}px;
`;
const ContentWrapper = styled.View`
  padding-horizontal: 16px;
  padding-bottom: 16px;
  padding-top: 12px;
`;

const AnimatedContentContainer = styled(Animated.View)`
  overflow: hidden;
`;

const Divider = styled(Animated.View)`
  height: 1px;
  background-color: ${({theme}: {theme: Theme}) => theme.border};
`;
const TitleText = styled(TextGeneric)`
  font-size: ${({theme}) => theme.text.s55}px;
  line-height: ${({theme}) => theme.text.s55 * 1.3}px;
  color: ${({theme}) => theme.text.dark};
  padding-right: ${({theme}) => theme.text.s55}px;
  flex: 1;
`;
const IconContainer = styled(Animated.View)`
  padding: ${({theme}) => theme.text.s8}px;
  border-width: 1px;
  border-color: ${({theme}) => theme.text.dark};
  border-radius: 100px;
`;
/**
 * AccordionItem component - Displays a collapsible content section with a header.
 *
 * @param {AccordionItemProps} props - Component props
 * @returns {React.ReactElement} The AccordionItem component
 */
const AccordionItem: React.FC<AccordionItemProps> = ({
  style,
  title,
  content,
  isOpen: initialIsOpen = false,
  onToggle,
}: AccordionItemProps): React.ReactElement => {
  const theme = useTheme() as Theme;
  const {hdp} = useResponsiveDimensions();
  const prevIsOpenRef = useRef<boolean>(initialIsOpen);

  // Main shared values
  const height = useSharedValue(0);
  const isExpanded = useSharedValue(initialIsOpen ? 1 : 0);
  const animationProgress = useSharedValue(initialIsOpen ? 1 : 0);

  // Animation durations and configs
  const ANIMATION_DURATION = 300;
  const SPRING_CONFIG = {
    damping: 15,
    stiffness: 120,
    mass: 1,
    overshootClamping: false,
  };

  // Update animation when isOpen prop changes
  useEffect(() => {
    if (prevIsOpenRef.current !== initialIsOpen) {
      animationProgress.value = withTiming(initialIsOpen ? 1 : 0, {
        duration: ANIMATION_DURATION,
        easing: Easing.bezierFn(0.25, 0.1, 0.25, 1),
      });
      isExpanded.value = initialIsOpen ? 1 : 0;
      prevIsOpenRef.current = initialIsOpen;
    }
  }, [initialIsOpen, animationProgress, isExpanded]);

  // Derived height for smooth animation with spring physics
  const derivedHeight = useDerivedValue(() => {
    return isExpanded.value === 1
      ? withSpring(height.value, SPRING_CONFIG)
      : withTiming(0, {
          duration: ANIMATION_DURATION,
          easing: Easing.bezierFn(0.25, 0.1, 0.25, 1),
        });
  });

  // Animation for content container
  const contentStyle = useAnimatedStyle(() => ({
    height: derivedHeight.value,
    opacity: interpolate(animationProgress.value, [0, 0.5, 1], [0, 0.5, 1]),
    transform: [
      {
        translateY: interpolate(
          animationProgress.value,
          [0, 1],
          [-theme.text.s12, 0],
        ),
      },
    ],
  }));

  // Animation for divider
  const dividerStyle = useAnimatedStyle(() => ({
    opacity: withDelay(
      isExpanded.value === 1 ? 0 : 100,
      withTiming(isExpanded.value, {
        duration: ANIMATION_DURATION / 2,
      }),
    ),
    width: withTiming(
      `${interpolate(animationProgress.value, [0, 1], [95, 100])}%`,
      {
        duration: ANIMATION_DURATION,
      },
    ),
    alignSelf: 'center',
  }));

  // Animation for container
  const containerStyle = useAnimatedStyle(() => {
    const backgroundColor = interpolateColor(
      animationProgress.value,
      [0, 1],
      [theme.homeBackground, theme.cardBackground],
    );

    const borderColor = interpolateColor(
      animationProgress.value,
      [0, 1],
      [theme.homeBackground, theme.border],
    );

    return {
      backgroundColor,
      borderColor,
      transform: [
        {
          scale: interpolate(animationProgress.value, [0, 1], [0.98, 1]),
        },
      ],
    };
  });

  // Animation for icon rotation
  const iconStyle = useAnimatedStyle(() => {
    const rotate = interpolate(animationProgress.value, [0, 1], [0, 45]);

    const backgroundColor = interpolateColor(
      animationProgress.value,
      [0, 1],
      [theme.cardBackground, theme.homeBackground],
    );

    const borderColor = interpolateColor(
      animationProgress.value,
      [0, 1],
      [theme.border, theme.homeBackground],
    );

    return {
      transform: [
        {
          rotate: `${rotate}deg`,
        },
        {
          scale: interpolate(animationProgress.value, [0, 1], [1, 1.1]),
        },
      ],
      backgroundColor,
      borderColor,
    };
  });

  // Handle content layout measurement
  const handleContentLayout = useCallback(
    (e: LayoutChangeEvent) => {
      height.value = e.nativeEvent.layout.height;
    },
    [height],
  );

  // Toggle accordion open/closed state
  const toggleAccordion = useCallback(() => {
    if (onToggle) {
      onToggle();
    }
  }, [onToggle]);

  return (
    <Container style={[style, containerStyle]} theme={theme}>
      <MyPressable onPress={toggleAccordion} borderRadius={8} secondaryRipple>
        <HeaderContainer>
          <TitleText bold variant="subtitle1">
            {title}
          </TitleText>
          <IconContainer style={iconStyle}>
            <Icon name={'plus'} size={theme.text.s55} color={theme.text.dark} />
          </IconContainer>
        </HeaderContainer>
      </MyPressable>

      <Divider style={dividerStyle} theme={theme} />

      <AnimatedContentContainer style={contentStyle}>
        <View
          onLayout={handleContentLayout}
          style={{position: 'absolute', width: '100%'}}>
          <ContentWrapper>
            {typeof content === 'string' ? (
              <TextGeneric
                variant="body2"
                color={theme.text.dark}
                style={{paddingTop: hdp(2)}}>
                {content}
              </TextGeneric>
            ) : (
              content
            )}
          </ContentWrapper>
        </View>
      </AnimatedContentContainer>
    </Container>
  );
};

export default AccordionItem;
